import { useCallback, useState } from "react";

import { AchievementToast } from "@/components/scenario/AchievementToast";
import { BaseballField } from "@/components/field/BaseballField";
import { CATEGORY_OPTIONS, DIFFICULTY_OPTIONS } from "@/constants/filters";
import { ExplanationPanel } from "@/components/scenario/ExplanationPanel";
import { LevelUpModal } from "@/components/scenario/LevelUpModal";
import { OptionButton } from "@/components/scenario/OptionButton";
import { ScenarioCard } from "@/components/scenario/ScenarioCard";
import { ErrorMessage } from "@/components/ui/ErrorMessage";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { useRandomScenario, useSubmitAnswer } from "@/hooks/use-scenarios";
import type { AchievementDefinition, AnswerResult, Scenario } from "@/types";
import { selectClass } from "@/constants/styles";
import { getErrorMessage } from "@/utils/error-message";
import { didLevelUp, getLevelForXp } from "@/utils/levels";
import { getOptionState } from "@/utils/option-state";
import type { Level } from "@/utils/levels";

type GameState = "answering" | "revealed";

export function Play() {
  const [difficulty, setDifficulty] = useState("");
  const [category, setCategory] = useState("");
  const [excludeIds, setExcludeIds] = useState<string[]>([]);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [gameState, setGameState] = useState<GameState>("answering");
  const [result, setResult] = useState<AnswerResult | null>(null);
  const [fetchKey, setFetchKey] = useState(0);
  const [levelUp, setLevelUp] = useState<Level | null>(null);
  const [newAchievements, setNewAchievements] = useState<AchievementDefinition[]>([]);

  const {
    data: scenario,
    isLoading,
    error,
  } = useRandomScenario(
    fetchKey,
    difficulty || undefined,
    category || undefined,
    excludeIds.length > 0 ? excludeIds : undefined,
  );

  const submitAnswer = useSubmitAnswer();

  const handleOptionSelect = useCallback(
    async (optionId: string, currentScenario: Scenario) => {
      if (gameState !== "answering") return;
      setSelectedOptionId(optionId);

      try {
        const response = await submitAnswer.mutateAsync({
          scenarioId: currentScenario.scenarioId,
          selectedOptionId: optionId,
        });
        setResult(response.result);
        setGameState("revealed");
        setExcludeIds((prev) => [...prev, currentScenario.scenarioId]);

        const previousXp = response.result.totalXp - response.result.xpEarned;
        if (didLevelUp(previousXp, response.result.totalXp)) {
          setLevelUp(getLevelForXp(response.result.totalXp));
        }
        if (response.result.newAchievements?.length > 0) {
          setNewAchievements(response.result.newAchievements);
        }
      } catch {
        setSelectedOptionId(null);
      }
    },
    [gameState, submitAnswer],
  );

  const handleNext = useCallback(() => {
    setSelectedOptionId(null);
    setResult(null);
    setGameState("answering");
    setNewAchievements([]);
    setFetchKey((k) => k + 1);
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row">
        <select
          value={difficulty}
          onChange={(e) => {
            setDifficulty(e.target.value);
            setExcludeIds([]);
            setFetchKey((k) => k + 1);
          }}
          className={selectClass}
        >
          {DIFFICULTY_OPTIONS.map((d) => (
            <option key={d.value} value={d.value}>{d.label}</option>
          ))}
        </select>

        <select
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setExcludeIds([]);
            setFetchKey((k) => k + 1);
          }}
          className={selectClass}
        >
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>

      {isLoading && <LoadingSpinner message="Loading scenario..." />}
      {error && <ErrorMessage message={getErrorMessage(error)} />}
      {submitAnswer.error && <ErrorMessage message={getErrorMessage(submitAnswer.error)} />}

      {scenario && (
        <div key={`${scenario.scenarioId}-${fetchKey}`} className="space-y-4">
          <div className="overflow-hidden rounded-xl shadow-sm">
            <BaseballField
              fieldState={scenario.fieldState}
              className="block w-full"
            />
          </div>

          <ScenarioCard scenario={scenario}>
            {scenario.options.map((option) => (
              <OptionButton
                key={option.id}
                label={option.text}
                state={getOptionState(option.id, result, selectedOptionId, gameState)}
                disabled={gameState === "revealed" || submitAnswer.isPending}
                onClick={() => handleOptionSelect(option.id, scenario)}
              />
            ))}
          </ScenarioCard>

          {gameState === "revealed" && result && (
            <ExplanationPanel
              correct={result.correct}
              explanation={result.explanation}
              streak={result.streak}
              xpEarned={result.xpEarned}
              onNext={handleNext}
            />
          )}
        </div>
      )}

      {newAchievements.length > 0 && <AchievementToast achievements={newAchievements} />}

      {levelUp && (
        <LevelUpModal level={levelUp} onDismiss={() => setLevelUp(null)} />
      )}
    </div>
  );
}
